import React from 'react';

import { Box, createStyles, makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';

const useStyles = makeStyles<
  Theme,
  { activeColor?: string; inactiveColor?: string; height?: number; checked?: boolean }
>((theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      display: 'flex',
      borderRadius: '8px',
      cursor: 'pointer',
      boxShadow: '2px 4px 12px rgba(20, 20, 43, 0.04)',
      overflow: 'hidden',
    },
    background: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: props => props.inactiveColor || '#E0E0E0',
    },
    activeBlock: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: '50%',
      backgroundColor: props => props.activeColor || '#673AB7',
      borderRadius: '8px',
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
      transform: props => (props.checked ? 'translateX(100%)' : 'translateX(0)'),
    },
    segmentsContainer: {
      position: 'relative',
      display: 'flex',
      width: '100%',
      zIndex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    segment: {
      flex: 1,
      padding: '16px 20px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: props => props.height || 80,
      transition: theme.transitions.create(['color'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    activeText: {
      color: '#FFFFFF',
    },
    inactiveText: {
      color: '#A0A0A0',
    },
  })
);

export interface AppSwitchOption {
  content: React.ReactNode;
}

export interface AppSwitchProps {
  leftOption: AppSwitchOption;
  rightOption: AppSwitchOption;
  checked: boolean;
  onChange: (checked: boolean) => void;
  activeColor?: string;
  inactiveColor?: string;
  height?: number;
  className?: string;
}

export const AppSwitch: React.FC<AppSwitchProps> = ({
  leftOption,
  rightOption,
  checked,
  onChange,
  activeColor,
  inactiveColor,
  height = 40,
  className,
}) => {
  const theme = useTheme();
  inactiveColor = inactiveColor || theme.palette.background.paper;
  activeColor = activeColor || theme.palette.primary.main;
  const classes = useStyles({ activeColor, inactiveColor, height, checked });

  const handleLeftClick = () => {
    onChange(false);
  };

  const handleRightClick = () => {
    onChange(true);
  };

  return (
    <Box className={clsx(classes.root, className)} height={height}>
      <Box className={classes.background} />
      <Box className={classes.activeBlock} />
      <Box className={classes.segmentsContainer}>
        <Box
          className={clsx(classes.segment, {
            [classes.activeText]: !checked,
            [classes.inactiveText]: checked,
          })}
          onClick={handleLeftClick}>
          {leftOption.content}
        </Box>
        <Box
          className={clsx(classes.segment, {
            [classes.activeText]: checked,
            [classes.inactiveText]: !checked,
          })}
          onClick={handleRightClick}>
          {rightOption.content}
        </Box>
      </Box>
    </Box>
  );
};
